/**
 * Shopify SEO Service
 * 
 * Handles all SEO-related operations including title, description,
 * and URL handle formatting according to Shopify Admin API 2025-01
 * specifications.
 */

export class ShopifySeoService {
  constructor(graphqlService) {
    this.graphqlService = graphqlService;
  }

  /**
   * Prepares SEO input for product creation or update
   * @param {object} product - Product data
   * @param {object} options - Additional SEO options
   * @returns {object} Formatted SEO input
   */
  prepareSeoInput(product, options = {}) {
    const seoInput = {
      title: this.formatSeoTitle(product.seoTitle || product.title, options),
      description: this.formatSeoDescription(product.seoDescription || product.descriptionHtml, options)
    };

    if (product.handle || options.handle) {
      seoInput.handle = this.formatHandle(product.handle || options.handle);
    }

    return seoInput;
  }

  /**
   * Updates SEO fields for an existing product
   * @param {string} productId - The product ID
   * @param {object} seoData - SEO data to update
   * @returns {Promise<object>} Updated product SEO data
   */
  async updateSeoFields(productId, seoData) {
    const updateSeoMutation = `
      mutation productUpdate($input: ProductInput!) {
        productUpdate(input: $input) {
          product {
            id
            title
            handle
            seo {
              title
              description
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const formattedSeo = this.prepareSeoInput(seoData);
    const input = {
      id: productId,
      seo: formattedSeo
    };

    const result = await this.graphqlService.mutate(updateSeoMutation, { input });

    if (result?.productUpdate?.userErrors?.length > 0) {
      throw new Error(`Failed to update SEO fields: ${JSON.stringify(result.productUpdate.userErrors)}`);
    }

    return result?.productUpdate?.product;
  }

  /**
   * Formats SEO title according to best practices
   * @param {string} title - Raw title
   * @param {object} options - Formatting options
   * @returns {string} Formatted SEO title
   */
  formatSeoTitle(title, options = {}) {
    if (!title) return '';

    let seoTitle = this.sanitizeString(title);

    // Truncate to Shopify's limit (70 characters)
    if (seoTitle.length > 70) {
      seoTitle = seoTitle.substring(0, 67) + '...';
    }

    // Add brand/store name if specified
    if (options.brandName && !seoTitle.includes(options.brandName)) {
      const separator = options.titleSeparator || ' | ';
      const combinedTitle = `${seoTitle}${separator}${options.brandName}`;
      
      // Ensure it still fits within 70 characters
      if (combinedTitle.length > 70) {
        return seoTitle;
      }
      return combinedTitle;
    }

    return seoTitle;
  }

  /**
   * Formats SEO description according to best practices
   * @param {string} description - Raw description
   * @param {object} options - Formatting options
   * @returns {string} Formatted SEO description
   */
  formatSeoDescription(description, options = {}) {
    if (!description) return '';

    // Remove HTML tags
    let seoDescription = description.replace(/<[^>]*>/g, '');
    
    // Basic sanitization
    seoDescription = this.sanitizeString(seoDescription);

    // Truncate to Shopify's limit (320 characters)
    if (seoDescription.length > 320) {
      seoDescription = seoDescription.substring(0, 317) + '...';
    }

    return seoDescription;
  }

  /**
   * Formats URL handle according to Shopify requirements
   * @param {string} handle - Raw handle
   * @returns {string} Formatted handle
   */
  formatHandle(handle) {
    if (!handle) return '';

    return this.sanitizeString(handle)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  }

  /**
   * Validates SEO data
   * @param {object} seoData - SEO data to validate
   * @returns {object} Validation result
   */
  validateSeoData(seoData) {
    const errors = [];

    if (seoData.title && seoData.title.length > 70) {
      errors.push('SEO title exceeds 70 characters');
    }

    if (seoData.description && seoData.description.length > 320) {
      errors.push('SEO description exceeds 320 characters');
    }

    if (seoData.handle) {
      const formattedHandle = this.formatHandle(seoData.handle);
      if (formattedHandle !== seoData.handle) {
        errors.push('Handle contains invalid characters');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Sanitizes a string value
   * @param {string} value - Value to sanitize
   * @returns {string} Sanitized value
   */
  sanitizeString(value) {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  }
}
